import { Button, Fade, Grid, IconButton, Modal } from '@material-ui/core'
import { Close } from '@material-ui/icons'
import { useEffect, useState } from 'react'
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux'
import { createPart, updatePart } from '../../actions/partActions'
import defaultPart from '../../assets/img/default-part.png'
import { useInput } from '../../hooks/useInput'
import CheckboxInput from '../Form/CheckboxInput'
import FileUploadTable from '../Form/FileUploadTable'
import FormContainer from '../Form/FormContainer'
import MultiFilesInput from '../Form/MultiFilesInput'
import NumberInput from '../Form/NumberInput'
import SelectInput from '../Form/SelectInput'
import TextAreaInput from '../Form/TextAreaInput'
import TextInput from '../Form/TextInput'
import ThumbnailUpload from '../Form/ThumbnailUpload'
import Loader from '../Loader/Loader'
import Message from '../Message/Message'
import HelpTooltip from '../Tooltip/HelpTooltip'

interface Props {
  setShowAddPart: (val: boolean) => void
  type: 'programming' | 'inspection'
  partToEdit?: Part
  setPartToEdit: (val: Part | undefined) => void
  partTitleOverride?: string
}

const AddEditPart = ({
  setShowAddPart,
  type,
  partToEdit,
  setPartToEdit,
  partTitleOverride,
}: Props) => {
  const dispatch = useDispatch()

  const [showWarningModal, setShowWarningModal] = useState(false)
  const [thumbnail, setThumbnail] = useState<Thumbnail | undefined>(undefined)
  const [thumbnailImage, setThumbnailImage] = useState()
  const [error, setError] = useState('')
  const {
    value: name,
    bind: bindName,
    error: nameError,
    setError: setNameError,
    setValue: setName,
  } = useInput('')
  const { value: partProgramLink, setValue: setPartProgramLink } =
    useInput(false)
  const [partProgramFiles, setPartProgramFiles] = useState<IFile[]>([])
  const [partProgramFilesError, setPartProgramFilesError] = useState('')
  const [partProgramFilesUploading, setPartProgramFilesUploading] = useState<
    IFile[]
  >([])
  const [additionalDocuments, setAdditionalDocuments] = useState<IFile[]>([])
  const [additionalDocumentsUploading, setAdditionalDocumentsUploading] =
    useState<IFile[]>([])
  const [additionalDocumentsError, setAdditionalDocumentsError] = useState('')
  const {
    value: material,
    bind: bindMaterial,
    setValue: setMaterial,
  } = useInput('Metal/Plastic')
  const [x, setX] = useState('')
  const [y, setY] = useState('')
  const [z, setZ] = useState('')
  const {
    value: units,
    bind: bindUnits,
    setValue: setUnits,
  } = useInput('inches')
  const {
    value: tolerance,
    bind: bindTolerance,

    setValue: setTolerance,
  } = useInput('< 0.250mm (0.010")')
  const [numOfCharacteristics, setNumOfCharacteristics] = useState('')
  const {
    value: availableToShip,
    bind: bindAvailableToShip,

    setValue: setAvailableToShip,
  } = useInput('')
  const {
    value: additionalNotes,
    bind: bindAdditionalNotes,
    setValue: setAdditionalNotes,
  } = useInput('')

  const { userInfo } = useSelector((state: RootStateOrAny) => state.userLogin)
  const { project } = useSelector(
    (state: RootStateOrAny) => state.projectDetails
  )
  const {
    error: partCreateError,
    success: createPartSuccess,
    loading: partCreateLoading,
  } = useSelector((state: RootStateOrAny) => state.partCreate)

  const {
    error: partUpdateError,
    success: partUpdateSuccess,
    loading: partUpdateLoading,
  } = useSelector((state: RootStateOrAny) => state.partUpdate)

  const toleranceOptionsInInches = [
    {
      value: '< 0.015mm (0.0005")',
      label: '< 0.0005"',
    },
    {
      value: '< 0.250mm (0.010")',
      label: '0.0005" - 0.010"',
    },
    {
      value: '< 0.500mm (0.020")',
      label: '0.010" - 0.020"',
    },
    {
      value: '> 0.500mm (0.020")',
      label: '> 0.020"',
    },
  ]

  const toleranceOptionsinMM = [
    {
      value: '< 0.015mm (0.0005")',
      label: '< 0.015mm',
    },
    {
      value: '< 0.250mm (0.010")',
      label: '0.015mm - 0.250mm',
    },
    {
      value: '< 0.500mm (0.020")',
      label: '0.250mm - 0.500mm',
    },
    {
      value: '> 0.500mm (0.020")',
      label: '> 0.500mm',
    },
  ]

  useEffect(() => {
    if (!!partToEdit) {
      setName(partToEdit.name)
      setPartProgramFiles(partToEdit.partProgramFiles)
      setPartProgramLink(!!partToEdit.partProgramLink)
      setAdditionalDocuments(partToEdit.additionalDocuments)
      setX(partToEdit.size ? partToEdit.size.x.toString() : '')
      setY(partToEdit.size ? partToEdit.size.y.toString() : '')
      setZ(partToEdit.size ? partToEdit.size.z.toString() : '')
      setUnits(partToEdit.size ? partToEdit.size.units : 'inches')
      setTolerance(partToEdit.tolerance)
      setNumOfCharacteristics(partToEdit.numOfCharacteristics.toString())
      setMaterial(partToEdit.material)
      setAvailableToShip(
        partToEdit.availableToShip !== undefined
          ? partToEdit.availableToShip === true
            ? 'Yes'
            : 'No'
          : ''
      )
      setAdditionalNotes(partToEdit.additionalNotes)
      setThumbnail(partToEdit.thumbnail)
    }
  }, [
    partToEdit,
    setName,
    setPartProgramLink,
    setX,
    setY,
    setZ,
    setUnits,
    setTolerance,
    setNumOfCharacteristics,
    setMaterial,
    setAvailableToShip,
    setAdditionalNotes,
    setThumbnail,
  ])

  useEffect(() => {
    if (createPartSuccess || partUpdateSuccess) {
      setShowAddPart(false)
    }

    if (thumbnail) {
      const getThumbnail = async () => {
        try {
          setThumbnailImage(
            // @ts-ignore
            `${process.env.REACT_APP_API}/api/thumbnail/${thumbnail.id}`
          )
        } catch (error) {
          console.error(error)
        }
      }

      getThumbnail()
    }
  }, [
    createPartSuccess,
    setShowAddPart,
    thumbnail,
    thumbnailImage,
    partUpdateSuccess,
  ])

  const formIsReady = () => {
    let ready = true

    setError('')

    if (name === '') {
      ready = false
      setNameError('Please enter a part name')
    } else {
      setNameError('')
    }

    if (type !== 'inspection') {
      if (partProgramFiles.length === 0 && !partProgramLink) {
        ready = false
        setPartProgramFilesError(
          'Please upload a part drawing file or select the option to send seperately'
        )
      } else {
        setPartProgramFilesError('')
      }

      // Show warning modal if these fields are empty
      // and retun false so errors do not display
      if (
        ready &&
        (numOfCharacteristics === '' || numOfCharacteristics === undefined)
      ) {
        setShowWarningModal(true)
        return false
      }
    }

    if (!ready) {
      setError('Please correct all form errors above')
    }

    return ready
  }

  const handleCreateNewPart = () => {
    if (formIsReady()) {
      createNewPart()
    }
  }

  const createNewPart = () => {
    const part = {
      user: userInfo.isAdmin ? project.user : userInfo._id,
      name,
      partProgramFiles,
      partProgramLink: partProgramLink ? 'Yes' : 'No',
      additionalDocuments,
      size: { x, y, z, units },
      tolerance,
      numOfCharacteristics,
      material,
      availableToShip: availableToShip === 'Yes' ? true : false,
      additionalNotes,
      thumbnail,
      type,
    }

    dispatch(createPart(part))
  }

  const saveUpdatePart = () => {
    let updatedPart = {
      ...partToEdit,
      name,
      partProgramFiles,
      partProgramLink,
      additionalDocuments,
      size: { x, y, z, units },
      tolerance,
      numOfCharacteristics,
      material,
      availableToShip: availableToShip === 'Yes' ? true : false,
      additionalNotes,
      thumbnail,
    }

    dispatch(updatePart(updatedPart))
  }

  const handleUpdatePart = () => {
    if (formIsReady()) {
      saveUpdatePart()
    }
  }

  const handleOverrideWarning = () => {
    setShowWarningModal(false)

    if (!partToEdit) {
      createNewPart()
    } else {
      saveUpdatePart()
    }
  }

  const handleCancelClick = () => {
    setShowAddPart(false)
    setPartToEdit(undefined)
  }

  return (
    <div className="add-part">
      <FormContainer>
        <h4 className="m-b-30">
          {userInfo.isAdmin
            ? partTitleOverride !== ''
              ? partTitleOverride
              : !partToEdit
              ? `Add new part for ${project.userName}`
              : `Edit part for ${project.userName}`
            : partTitleOverride !== ''
            ? partTitleOverride
            : !partToEdit
            ? 'Add new part'
            : 'Edit part'}
        </h4>
        <Grid container spacing={4} className="p-t-0 p-b-0">
          <Grid item xs={12}>
            <TextInput
              name="name"
              label="Part name"
              bindValue={bindName}
              error={nameError}
            />
          </Grid>

          {type !== 'inspection' && (
            <Grid item xs={12} className="p-t-0 p-b-0">
              <MultiFilesInput
                disabled={null}
                name="partProgramFiles"
                label="Part drawing file(s)"
                filesUploading={partProgramFilesUploading}
                setFiles={setPartProgramFiles}
                setFilesUploading={setPartProgramFilesUploading}
                contentTop={
                  <>
                    Drag and drop or click to add
                    <strong> Part files</strong>
                  </>
                }
                contentBottom="Part drawing filetypes can go here."
                error={partProgramFilesError}
                setError={setPartProgramFilesError}
              />
              {(partProgramFiles.length > 0 ||
                partProgramFilesUploading.length > 0) && (
                <FileUploadTable
                  data={partProgramFiles}
                  setData={setPartProgramFiles}
                  filesUploading={partProgramFilesUploading}
                />
              )}

              <CheckboxInput
                name={''}
                value={!!partProgramLink}
                setValue={setPartProgramLink}
                label={
                  <div className="fs14 m-t-10 m-b-0">
                    Don't have the files at the moment or need to send a link?
                    <HelpTooltip
                      title={
                        <div className="p-b-12">
                          Select this option if part drawing files are not
                          available to you at the moment or you need to send
                          them through a secure file sharing service. An
                          Application Engineer will reach out to you collect
                          what is needed.
                        </div>
                      }
                    />
                  </div>
                }
              />
            </Grid>
          )}

          <Grid item xs={12} className="p-t-0 p-b-0">
            <MultiFilesInput
              disabled={null}
              name="additionalDocuments"
              label="Program, CAD, or other documents (optional)"
              filesUploading={additionalDocumentsUploading}
              setFiles={setAdditionalDocuments}
              setFilesUploading={setAdditionalDocumentsUploading}
              error={additionalDocumentsError}
              setError={setAdditionalDocumentsError}
              contentTop={
                <>
                  Drag and drop or click to add
                  <strong> additional files</strong>
                </>
              }
              contentBottom="CAD, Program, PDF, and image filetypes can go here."
            />
            {(additionalDocuments.length > 0 ||
              additionalDocumentsUploading.length > 0) && (
              <div className={type === 'programming' ? 'm-t-32' : ''}>
                <FileUploadTable
                  data={additionalDocuments}
                  setData={setAdditionalDocuments}
                  filesUploading={additionalDocumentsUploading}
                />
              </div>
            )}
          </Grid>

          {type !== 'inspection' && (
            <>
              <Grid item sm={6} className="p-t-0">
                <label htmlFor="characteristics">
                  Number of characteristics
                  <HelpTooltip
                    title={
                      <p>
                        Enter the number of characteristics you need measured.
                        The number of characteristics helps to determine the
                        estimated costs.
                      </p>
                    }
                  />
                </label>

                <NumberInput
                  label={null}
                  error={null}
                  value={numOfCharacteristics}
                  setValue={setNumOfCharacteristics}
                />
              </Grid>
              <Grid item sm={6} className="p-t-0">
                <SelectInput
                  label="Is this part available to be shipped?"
                  name="material"
                  options={[
                    { value: 'Yes', label: 'Yes' },
                    { value: 'No', label: 'No' },
                  ]}
                  bindValue={bindAvailableToShip}
                />
              </Grid>
              <Grid item xs={6} className="p-t-0 p-b-0">
                <SelectInput
                  label="Units"
                  name="units"
                  options={[
                    { value: 'inches', label: 'Inches' },
                    { value: 'mm', label: 'Millimeters' },
                  ]}
                  bindValue={bindUnits}
                />
              </Grid>

              <Grid item xs={6} className="p-t-0 p-b-0">
                <div>
                  <label htmlFor="size">
                    Part dimensions
                    <HelpTooltip
                      title={
                        <p>
                          Enter the length, width, and height of your part and
                          select the appropriate unit of measurement. If part
                          dimensions are not provided, part is assumed to be no
                          larger than 12in (300mm) volume and no smaller than a
                          3in (75mm) volume.
                        </p>
                      }
                    />
                  </label>
                </div>
                <Grid container spacing={3}>
                  <Grid item xs={4} className="p-b-0">
                    <NumberInput
                      error={null}
                      value={x}
                      setValue={setX}
                      label="X"
                    />
                  </Grid>
                  <Grid item xs={4} className="p-b-0">
                    <NumberInput
                      error={null}
                      value={y}
                      setValue={setY}
                      label="Y"
                    />
                  </Grid>
                  <Grid item xs={4} className="p-b-0">
                    <NumberInput
                      error={null}
                      value={z}
                      setValue={setZ}
                      label="Z"
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item sm={6} className="p-b-0">
                <SelectInput
                  label="Tolerance"
                  name="tolerance"
                  options={
                    units === 'inches'
                      ? toleranceOptionsInInches
                      : toleranceOptionsinMM
                  }
                  bindValue={bindTolerance}
                />
              </Grid>
              <Grid item sm={6} className="p-b-0">
                <SelectInput
                  label="Part material"
                  name="material"
                  options={[
                    { value: 'Metal/Plastic', label: 'Metal/Plastic' },
                    { value: 'Plastic Molded', label: 'Plastic Molded' },
                    {
                      value: 'Glass/Plastic (transparent)',
                      label: 'Glass/Plastic (transparent)',
                    },
                    {
                      value: 'Other',
                      label: 'Other',
                    },
                  ]}
                  bindValue={bindMaterial}
                />
              </Grid>
            </>
          )}

          <Grid item sm={6} className={type === 'inspection' ? 'p-t-0' : ''}>
            <TextAreaInput
              name="additionalNotes"
              label="Additional notes (optional)"
              bindValue={bindAdditionalNotes}
              rows={4}
              className="tall"
            />
          </Grid>

          <Grid item sm={6} className={type === 'inspection' ? 'p-t-0' : ''}>
            <ThumbnailUpload
              defaultImage={defaultPart}
              setThumbnail={setThumbnail}
              setThumbnailImage={setThumbnailImage}
              thumbnailImage={thumbnailImage}
            />
          </Grid>
        </Grid>
        <div className="m-t-40 flex flex-end">
          <div>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleCancelClick}
              disabled={partCreateLoading || partUpdateLoading}
            >
              Cancel
            </Button>

            {!partToEdit ? (
              <Button
                variant="contained"
                color="primary"
                style={{ marginLeft: 20 }}
                onClick={handleCreateNewPart}
                disabled={partCreateLoading || partUpdateLoading}
                className={
                  partCreateLoading || partUpdateLoading ? 'loading' : ''
                }
              >
                Add Part
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                style={{ marginLeft: 20 }}
                onClick={handleUpdatePart}
                disabled={partCreateLoading || partUpdateLoading}
                className={
                  partCreateLoading || partUpdateLoading ? 'loading' : ''
                }
              >
                {partCreateLoading || partUpdateLoading ? (
                  <Loader />
                ) : (
                  'Update part'
                )}
              </Button>
            )}
          </div>
        </div>
        {error && <Message variant="error">{error}</Message>}
        {partCreateError && (
          <Message variant="error">{partCreateError}</Message>
        )}
        {partUpdateError && (
          <Message variant="error">{partUpdateError}</Message>
        )}
      </FormContainer>

      <Modal open={showWarningModal} onClose={() => setShowWarningModal(false)}>
        <Fade in={showWarningModal}>
          <div className="modal-window">
            <div className="modal-header">
              <div>Details needed</div>
              <IconButton onClick={() => setShowWarningModal(false)}>
                <Close />
              </IconButton>
            </div>
            <div className="modal-body">
              <h4 className="m-b-32">Details needed</h4>
              <p>
                Without the following information we can't provide an immediate
                estimated quote:
              </p>
              <ul>
                {!numOfCharacteristics && <li>Number of characteristics</li>}
              </ul>
              <p>Would you like to continue?</p>
              <div className="actions m-t-40 flex-end flex">
                <Button
                  color="primary"
                  variant="outlined"
                  className="m-r-24"
                  disabled={partCreateLoading || partUpdateLoading}
                  onClick={() => setShowWarningModal(false)}
                >
                  No, add more info
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={handleOverrideWarning}
                  disabled={partCreateLoading || partUpdateLoading}
                  className={
                    partCreateLoading || partUpdateLoading ? 'loading' : ''
                  }
                >
                  {partCreateLoading || partUpdateLoading ? (
                    <Loader />
                  ) : (
                    'Yes, continue and save part'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </Fade>
      </Modal>
    </div>
  )
}

export default AddEditPart
